<script>
import { Input } from "@/shared/ui/input";
import { FormSheet } from "@/shared/ui/form-sheet";
import { Search } from "lucide-vue-next";

export default {
  name: "NotaryActionPicker",
  components: {
    FormSheet,
    Input,
    Search,
  },

  props: {
    title: {
      type: String,
      default: "",
    },
    actions: {
      type: Array,
      default: () => [],
    },
    groupName: {
      type: String,
      default: "",
    },
  },

  emits: ["close", "select"],

  data() {
    return {
      visible: false,
      query: "",
    };
  },

  computed: {
    locale() {
      return this.$i18n?.locale || "ru";
    },
    filteredActions() {
      const q = (this.query || "").trim().toLowerCase();
      if (!q) return this.actions || [];
      return (this.actions || []).filter((item) =>
        this.displayName(item).toLowerCase().includes(q)
      );
    },
  },

  mounted() {
    this.visible = true;
  },

  methods: {
    displayName(item) {
      if (!item) return "";
      const l = this.locale;
      if (l === "kk" || l === "kz")
        return item.name_kz || item.name || item.code;
      if (l === "en") return item.name_en || item.name || item.code;
      return item.name || item.code;
    },

    pick(item) {
      this.$emit("select", item);
      this.handleClose();
    },

    handleClose() {
      this.visible = false;
      this.$emit("close");
    },
  },
};
</script>

<template>
  <FormSheet
    v-model:open="visible"
    :title="title || $t('Выберите нотариальное действие')"
    :width="500"
    :show-footer="false"
    @close="handleClose"
  >
    <div class="w-full h-full flex flex-col gap-6">
      <div class="w-full flex flex-col gap-2 flex-shrink-0">
        <div class="w-full flex gap-3">
          <div class="relative flex-1">
            <Search
              class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
            />
            <Input
              v-model="query"
              :placeholder="$t('Поиск по названию')"
              class="pl-9"
            />
          </div>
        </div>
      </div>

      <main class="flex-1 overflow-y-auto w-full flex flex-col gap-6 pb-4">
        <div
          v-if="!filteredActions.length"
          class="text-center text-muted-foreground py-10"
        >
          {{ $t("Ничего не найдено") }}
        </div>

        <div v-else class="group">
          <div
            v-if="groupName"
            class="text-muted-foreground text-sm font-medium leading-5"
          >
            {{ groupName }}
          </div>

          <ul>
            <li v-for="item in filteredActions" :key="item.code">
              <div
                class="w-full flex justify-between items-center cursor-pointer"
                @click="pick(item)"
              >
                <div
                  class="flex-1 text-foreground text-sm font-medium leading-5 line-clamp-2"
                >
                  {{ displayName(item) }}
                </div>
                <svg-icon
                  name="huge-chevron-right"
                  class="w-4 h-4 flex-shrink-0"
                />
              </div>
            </li>
          </ul>
        </div>
      </main>
    </div>
  </FormSheet>
</template>

<style scoped lang="scss">
.group {
  @apply w-full flex flex-col justify-start items-start gap-4;

  ul {
    @apply w-full border border-solid border-border flex flex-col justify-start items-start overflow-hidden;
    border-radius: var(--radius);

    li {
      @apply w-full px-4 py-3 bg-card border-b border-border flex flex-col justify-center items-start gap-4;

      &:last-child {
        @apply border-b-0;
      }

      &:hover {
        @apply bg-muted;
      }
    }
  }
}
</style>
